// THE ADDRESS BOX'S SUGGESTIONS — APPLE MAPKIT JS (WEB-SUGGEST, 2026-09-29).
//
// Tyler, 2026-09-29, on the paid website's first page: "address should auto populate on this initial page." The free
// check on proppeal.com already does (its `free-check-suggest.js`, FIX-1); this is the same behaviour ported to this
// package, so both doors suggest the same addresses the same way. `routes/Entry.tsx` draws it as a combobox.
//
// WHAT IT SENDS, AND TO WHOM.
//   · /api/mapkit-token (this site's own Pages Function, `web/functions/`) — when `/` opens, to learn whether
//     suggestions are on and get a short-lived token; again only when MapKit JS asks for a fresh one. It carries nothing
//     the owner typed. With no MapKit key on the project the answer says so and the page keeps its plain address box:
//     nothing is loaded from Apple.
//   · Apple — MapKit JS itself (`MAPKIT_SRC`): on `/` AS SOON AS THE ADDRESS BOX APPEARS (WALK-FIX-2, below), and for
//     every other address box when it gets focus; then, as the owner types, what they have typed, for suggestions (after
//     `DEBOUNCE_MS` without a keystroke, `MIN_CHARS` or more, at most `MAX_LOOKUPS` a page); and, when they choose one
//     whose ZIP code the suggestion lacks, that one suggestion to fill in the ZIP (at most `MAX_PLACES`). Loading the
//     script and initialising it sends Apple nothing the owner typed (the approved privacy sentence is "the address box
//     sends what you type to Apple to suggest addresses", docs/legal/WEB_PURCHASE_POLICIES.md), and the satellite view
//     on `/details` already loads the same script with no focus at all. The browser's location is never asked for
//     (`getsUserLocation: false`) and results are limited to the United States.
//
// WALK-FIX-2 (Tyler's second walk, 2026-10-05: "the address auto populate is a little laggy"). Four changes, none of
// which asks Apple more than the caps below allow — the caps are unchanged (60 lookups, 20 ZIP lookups, 6 tokens a
// page load) and still end suggestions for the page when reached:
//   · `DEBOUNCE_MS` is 150 ms, from 300;
//   · `/` warms MapKit JS and its token when the box appears (`AddressCombobox`'s `warm`), not on the first lookup. The
//     token `prepare` fetched is the one MapKit JS's first authorisation uses, so warming asks for no extra token;
//   · while a NARROWER query is waiting for Apple, the last answer is filtered here, at once (`matchesTyped`), so the
//     list follows the keystroke instead of waiting on the network;
//   · and nothing blanks the list while a lookup is out: the old list stays until Apple answers (a failed lookup, fewer
//     than `MIN_CHARS`, or suggestions switching off still close it).
//   · Our server — unchanged: the address and the unit, as the two boxes read when the button is pressed. A chosen
//     suggestion is just text in the box: no place ID, no coordinate, nothing else from Apple rides along.
//
// THE UNIT BOX IS NEVER WIPED BY A PICK (R162; the site's CONDO-1 rule). Apple suggests the BUILDING, without the unit.
// Choosing a suggestion leaves whatever the owner put in the Unit box exactly as typed; when the box is empty and the
// text the owner had typed names a unit ("… Unit 1512"), that unit moves into the box, where they can see it survived,
// and the address box takes the building alone. WHICH text "names a unit" is not decided here: it is the server's own
// `unitInForce` (functions/src/v2/resolve/typedUnit.ts, R115's one parser), the function `v2ResolveProperty` reads the
// same two boxes with. So a pick moves into the box exactly the unit the server would have read from the address line,
// and no unit vocabulary is written into this package.
//
// ANY FAILURE SWITCHES SUGGESTIONS OFF FOR THE PAGE, SILENTLY: a missing key or a refused origin (the token function's
// named refusal), a script that will not load, MapKit JS's own error, three lookups failing in a row, a cap reached.
// The owner keeps a plain box that works exactly as before.
//
// Everything that decides something is exported, and the world (fetch, the script loader, the clock, timers) comes in
// as arguments, so web/test/addressSuggest.test.tsx drives it with a fake fetch and a fake mapkit.

import { typedUnitDesignator, unitInForce } from "@v2-typed-unit";

/** Our own token function, on this origin (`web/functions/api/mapkit-token.ts`). */
export const TOKEN_PATH = "/api/mapkit-token";
/**
 * Apple's MapKit JS core, pinned to major version 6 (Apple: "Loading the latest version of MapKit JS") — the same
 * script the site's free check loads. The one third-party script this package loads, and only on focus.
 */
export const MAPKIT_SRC = "https://cdn.apple-mapkit.com/mk/6/mapkit.core.js";
/** Quiet time after the last keystroke before Apple is asked: 150 ms (WALK-FIX-2; it was 300). */
export const DEBOUNCE_MS = 150;
/** Fewer characters than this ask nobody. */
export const MIN_CHARS = 3;
/** Caps per page load: suggestion lookups, lookups to complete a chosen suggestion, and token requests. */
export const MAX_LOOKUPS = 60;
export const MAX_PLACES = 20;
export const MAX_TOKENS = 6;
/** At most this many suggestions are shown. */
export const MAX_SHOWN = 6;
/** Lookups failing in a row before suggestions switch off for the page. */
export const MAX_FAILURES = 3;
/** A token this close to its expiry (seconds) is not handed to MapKit JS; a fresh one is asked for. */
export const TOKEN_MARGIN_S = 60;
/** The options every search is made with: addresses in the United States, and nothing about where the browser is. */
export const SEARCH_OPTIONS = {
  getsUserLocation: false,
  limitToCountries: "us",
  includeAddresses: true,
  includePointsOfInterest: false,
  includePhysicalFeatures: false,
  includeQueries: false,
  language: "en",
} as const;
/**
 * THE SEARCH'S FIXED ANCHOR, the site's: the middle of the contiguous United States (near Lebanon, Kansas), not the
 * visitor. Measured on the live site 2026-09-28: with no anchor Apple answers a house number with towns and bare
 * streets; with this one it answers the house first. A fixed point sends Apple nothing about who is typing.
 */
export const ANCHOR = { latitude: 39.83, longitude: -98.58 } as const;

// ── what MapKit JS hands back, as far as this module reads it ──────────────────────────────────────────────────────

export interface MapKitPlace {
  readonly countryCode?: string;
  readonly fullThoroughfare?: string;
  readonly subThoroughfare?: string;
  readonly thoroughfare?: string;
  readonly locality?: string;
  readonly administrativeAreaCode?: string;
  readonly postCode?: string;
}
export interface MapKitAutocompleteResult extends MapKitPlace {
  readonly displayLines?: readonly string[];
}
export interface MapKitSearch {
  autocomplete(query: string, options?: { signal?: AbortSignal }): Promise<{ results?: readonly MapKitAutocompleteResult[] }>;
  search(query: unknown): Promise<{ places?: readonly MapKitPlace[] }>;
}
export interface MapKit {
  init(options: { authorizationCallback: (done: (token: string) => void) => void; language: string }): void;
  load?(libraries: string[]): unknown;
  addEventListener?(type: string, listener: () => void): void;
  Search?: new (options: Record<string, unknown>) => MapKitSearch;
  Coordinate?: new (latitude: number, longitude: number) => unknown;
}

// ── pure: what a suggestion says, and the address it stands for ────────────────────────────────────────────────────

function text(v: unknown): string {
  return typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";
}

/**
 * THE ONE-LINE ADDRESS: "street, city, ST ZIP" from a MapKit place or autocomplete result, or null when it lacks the
 * street, the city or the state, or is outside the United States. The ZIP is added when it is known.
 */
export function oneLine(p: MapKitPlace | null | undefined): string | null {
  if (!p || typeof p !== "object") return null;
  const country = text(p.countryCode);
  if (country && country.toUpperCase() !== "US") return null;
  const street = text(p.fullThoroughfare) || [text(p.subThoroughfare), text(p.thoroughfare)].filter(Boolean).join(" ");
  const city = text(p.locality);
  const state = text(p.administrativeAreaCode);
  const zip = text(p.postCode);
  if (!street || !city || !state) return null;
  return `${street}, ${city}, ${state}${zip ? ` ${zip}` : ""}`;
}

/** What the list shows for a suggestion: Apple's own display lines on one line, without the country. */
export function labelFor(result: MapKitAutocompleteResult | null | undefined): string | null {
  const lines = result && Array.isArray(result.displayLines) ? result.displayLines.map(text).filter(Boolean) : [];
  if (!lines.length) return null;
  return lines.join(", ").replace(/,?\s*(United States|USA|US)$/i, "").trim() || null;
}

/** The words of a query or a label, for `matchesTyped`: lower case, split on anything that is not a letter or digit. */
function words(v: string): string[] {
  return v.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w !== "");
}

/**
 * WOULD THIS SUGGESTION STILL FIT WHAT IS TYPED? Every typed word begins some word of the label ("226 W Ritt" fits
 * "226 W Rittenhouse Sq, Philadelphia, PA"). Used only to narrow the LAST answer while Apple is asked about the newer
 * text: Apple's answer replaces it either way, so a label this test drops that Apple would have kept ("West" typed for
 * "W") is back as soon as the answer lands.
 */
export function matchesTyped(label: string, typed: string): boolean {
  const have = words(label);
  return words(typed).every((w) => have.some((h) => h.startsWith(w)));
}

/** Is `next` the same query as `prev`, or `prev` with more typed after it (case and spacing aside)? */
function extendsQuery(next: string, prev: string): boolean {
  return next.toLowerCase().startsWith(prev.toLowerCase());
}

/** Has this one-line address a ZIP code at its end? */
export function hasZip(address: string): boolean {
  return /\b\d{5}(-\d{4})?$/.test(address);
}

/**
 * WHAT THE UNIT BOX HOLDS ONCE A SUGGESTION IS CHOSEN (R162, the site's `unitAfterPick`).
 *   · NEVER LESS THAN IT HELD: whatever the owner put in the box stays, exactly as typed.
 *   · When the box is empty and the text the owner had typed names a unit, that unit's value moves into the box —
 *     read by the server's own `unitInForce`, so it is the value the server would have lifted from the address line.
 *   · Nothing else is ever put in it.
 */
export function unitAfterPick(typed: string, box: string): string {
  return unitInForce(typed, box)?.value ?? box;
}

/**
 * A ONE-LINE ADDRESS KEEPS THE UNIT THE OWNER TYPED (the mailing address on `/you`, Tyler 2026-09-30: "this page should
 * also do auto populate for addresses.. but typing can override them"). A box with no Unit box beside it has nowhere to
 * move a unit to, so a pick must not drop it: the designator the owner typed ("Apt 4", "#704", in their own spelling,
 * read by the server's `typedUnitDesignator`) goes back after the picked street, before the first comma. A picked line
 * that already names it is left as it is.
 */
export function keepUnitInLine(picked: string, typed: string): string {
  const designator = typedUnitDesignator(typed);
  if (designator === null || picked.toUpperCase().includes(designator.toUpperCase())) return picked;
  const comma = picked.indexOf(",");
  return comma < 0 ? `${picked} ${designator}` : `${picked.slice(0, comma)} ${designator}${picked.slice(comma)}`;
}

// ── the suggester ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface SuggestItem {
  readonly label: string;
  readonly result: MapKitAutocompleteResult;
}

export type SuggesterState = "unknown" | "on" | "off";

export interface TokenResponse {
  readonly ok: boolean;
  json(): Promise<unknown>;
}

export interface SuggesterDeps {
  fetch(url: string, init: RequestInit): Promise<TokenResponse>;
  loadMapKit(): Promise<MapKit>;
  now?(): number;
  setTimeout(f: () => void, ms: number): unknown;
  clearTimeout(t: unknown): void;
  AbortController?: typeof AbortController;
}

export interface AddressSuggester {
  /** Asks the token function once: true when suggestions are on for this page. */
  prepare(): Promise<boolean>;
  /** Loads MapKit JS, initialises it with a token callback, loads its search library and makes the Search: on focus, or
   *  when `/`'s box appears (WALK-FIX-2). Once; later calls share it. */
  start(): Promise<MapKitSearch | null>;
  /** Suggestions for what is typed: `show(items)` once the owner has paused, or `show([])`. A newer keystroke cancels an
   *  older lookup, and a late answer is dropped. WALK-FIX-2: text that extends the last answered query is shown that
   *  answer, narrowed, at once; otherwise nothing is shown until Apple answers, so the list on screen stays. */
  suggest(typed: string, show: (items: SuggestItem[]) => void): void;
  /** What the address box holds the moment a suggestion is chosen: its label (the building). */
  fill(item: SuggestItem): string;
  /** The one-line address for a chosen suggestion, with its ZIP when Apple has one. */
  complete(item: SuggestItem): Promise<string>;
  cancel(): void;
  off(): void;
  state(): SuggesterState;
  /** Called once if suggestions switch off. Returns the unsubscribe. */
  onOff(listener: () => void): () => void;
  counts(): { tokens: number; lookups: number; places: number };
}

export function createSuggester(deps: SuggesterDeps): AddressSuggester {
  let state: SuggesterState = "unknown";
  let preparing: Promise<boolean> | null = null;
  let starting: Promise<MapKitSearch | null> | null = null;
  let search: MapKitSearch | null = null;
  /** The Search that resolves a chosen suggestion: the same options WITHOUT the anchor, which Apple answers with a 400
   *  when resolving (measured on the live site, 2026-09-28). */
  let resolver: MapKitSearch | null = null;
  let spare: { token: string; expiresAt: number } | null = null;
  let tokensAsked = 0;
  let lookups = 0;
  let places = 0;
  let failures = 0;
  let timer: unknown = null;
  let pending: AbortController | null = null;
  let seq = 0;
  /** Apple's last answer and the text it answered, for narrowing at once while a newer lookup is out (WALK-FIX-2). */
  let last: { q: string; items: SuggestItem[] } | null = null;
  const listeners = new Set<() => void>();

  const nowSeconds = (): number => Math.floor((deps.now ? deps.now() : Date.now()) / 1000);

  async function fetchToken(): Promise<{ token: string; expiresAt: number }> {
    if (tokensAsked >= MAX_TOKENS) throw new Error("token cap");
    tokensAsked += 1;
    return requestMapKitToken(deps.fetch);
  }

  function cancel(): void {
    seq += 1;
    if (timer !== null) deps.clearTimeout(timer);
    timer = null;
    if (pending) {
      try {
        pending.abort();
      } catch {
        /* nothing to cancel */
      }
    }
    pending = null;
  }

  function off(): void {
    if (state === "off") return;
    state = "off";
    last = null;
    cancel();
    for (const listener of [...listeners]) {
      try {
        listener();
      } catch {
        /* the page keeps whatever box it has */
      }
    }
  }

  function prepare(): Promise<boolean> {
    if (!preparing) {
      preparing = fetchToken().then(
        (t) => {
          if (state === "unknown") state = "on";
          spare = t;
          return state === "on";
        },
        () => {
          off();
          return false;
        },
      );
    }
    return preparing;
  }

  /** MapKit JS's `authorizationCallback`: the token `prepare` fetched while it is fresh, then a new one each time. */
  function authorize(done: (token: string) => void): void {
    const t = spare;
    spare = null;
    if (t && t.expiresAt - nowSeconds() > TOKEN_MARGIN_S) {
      done(t.token);
      return;
    }
    fetchToken().then((fresh) => done(fresh.token), off);
  }

  function start(): Promise<MapKitSearch | null> {
    if (!starting) {
      starting = (async () => {
        if (!(await prepare())) return null;
        const mapkit = await deps.loadMapKit();
        if (!mapkit || typeof mapkit.init !== "function") throw new Error("MapKit JS loaded without its API");
        if (typeof mapkit.addEventListener === "function") mapkit.addEventListener("error", off);
        initMapKit(mapkit, authorize);
        if (typeof mapkit.load === "function") await mapkit.load(["services"]);
        const Search = mapkit.Search;
        if (state !== "on" || typeof Search !== "function") throw new Error("no search");
        const anchored: Record<string, unknown> = { ...SEARCH_OPTIONS };
        if (typeof mapkit.Coordinate === "function") anchored.coordinate = new mapkit.Coordinate(ANCHOR.latitude, ANCHOR.longitude);
        search = new Search(anchored);
        resolver = new Search({ ...SEARCH_OPTIONS });
        return search;
      })().catch(() => {
        off();
        return null;
      });
    }
    return starting;
  }

  function itemsFrom(response: { results?: readonly MapKitAutocompleteResult[] } | null | undefined): SuggestItem[] {
    const seen = new Set<string>();
    const out: SuggestItem[] = [];
    const results = response && Array.isArray(response.results) ? response.results : [];
    for (const result of results) {
      if (out.length >= MAX_SHOWN) break;
      const label = labelFor(result);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push({ label, result });
    }
    return out;
  }

  function suggest(typed: string, show: (items: SuggestItem[]) => void): void {
    cancel();
    const mine = seq;
    const q = text(typed);
    if (state === "off" || q.length < MIN_CHARS) {
      show([]);
      return;
    }
    // WALK-FIX-2: the same text, or more typed after it, narrows Apple's last answer NOW. When nothing in it fits, the
    // list on screen stays as it is until Apple answers: it is never blanked while a lookup is out.
    if (last !== null && extendsQuery(q, last.q)) {
      const kept = q.length === last.q.length ? last.items : last.items.filter((item) => matchesTyped(item.label, q));
      if (kept.length > 0) show(kept);
    }
    timer = deps.setTimeout(() => {
      timer = null;
      if (lookups >= MAX_LOOKUPS) {
        off();
        show([]);
        return;
      }
      void start().then((s) => {
        if (!s || mine !== seq || state !== "on") return;
        lookups += 1;
        const ctl = deps.AbortController ? new deps.AbortController() : null;
        pending = ctl;
        let asked: Promise<{ results?: readonly MapKitAutocompleteResult[] }>;
        try {
          asked = Promise.resolve(s.autocomplete(q, ctl ? { signal: ctl.signal } : undefined));
        } catch (e) {
          asked = Promise.reject(e);
        }
        asked.then(
          (response) => {
            if (pending === ctl) pending = null;
            if (mine !== seq) return;
            failures = 0;
            const items = itemsFrom(response);
            last = { q, items };
            show(items);
          },
          (err: unknown) => {
            if (pending === ctl) pending = null;
            if (mine !== seq || (err instanceof Error && err.name === "AbortError")) return;
            failures += 1;
            if (failures >= MAX_FAILURES) off();
            show([]);
          },
        );
      });
    }, DEBOUNCE_MS);
  }

  function fill(item: SuggestItem): string {
    return item.label;
  }

  async function complete(item: SuggestItem): Promise<string> {
    const direct = oneLine(item.result);
    if (direct && hasZip(direct)) return direct;
    const by = resolver ?? search;
    if (!by || state !== "on" || places >= MAX_PLACES) return direct ?? item.label;
    places += 1;
    try {
      const response = await by.search(item.result);
      const found = response && Array.isArray(response.places) ? response.places : [];
      return oneLine(found[0]) ?? direct ?? item.label;
    } catch {
      return direct ?? item.label;
    }
  }

  return {
    prepare,
    start,
    suggest,
    fill,
    complete,
    cancel,
    off,
    state: () => state,
    onOff(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    counts: () => ({ tokens: tokensAsked, lookups, places }),
  };
}

// ── the browser ───────────────────────────────────────────────────────────────────────────────────────────────────

type WithMapKit = { mapkit?: MapKit };

/**
 * ONE TOKEN FROM OUR OWN FUNCTION, or a throw when it has none to give (no key on the project, a refused origin, a
 * failed request). Shared with the step-1 satellite view (`./satellite.ts`, WEB-PARITY-1C): one token function, one way
 * of asking it, and it carries nothing about the owner.
 */
export async function requestMapKitToken(
  fetch: (url: string, init: RequestInit) => Promise<TokenResponse>,
): Promise<{ token: string; expiresAt: number }> {
  const res = await fetch(TOKEN_PATH, { method: "GET", mode: "same-origin", credentials: "same-origin", cache: "no-store" });
  if (!res || !res.ok) throw new Error("no token");
  const body = (await res.json()) as { available?: unknown; token?: unknown; expiresAt?: unknown } | null;
  if (!body || body.available !== true || typeof body.token !== "string" || !body.token) throw new Error("no token");
  return { token: body.token, expiresAt: typeof body.expiresAt === "number" ? body.expiresAt : 0 };
}

const initialised = new WeakSet<object>();

/**
 * MapKit JS is initialised ONCE A PAGE, by whichever of its two users gets there first — the address box on `/` or the
 * satellite view on `/details` (WEB-PARITY-1C). Both authorise through `requestMapKitToken`, so either callback serves
 * the other; a second `init` would only replace the first.
 */
export function initMapKit(mapkit: MapKit, authorize: (done: (token: string) => void) => void): void {
  if (initialised.has(mapkit)) return;
  initialised.add(mapkit);
  mapkit.init({ authorizationCallback: authorize, language: "en" });
}

/** Apple's script, added to the page once, the way Apple's own documentation loads it on demand. */
export function loadMapKitScript(): Promise<MapKit> {
  return new Promise((resolve, reject) => {
    const already = (window as unknown as WithMapKit).mapkit;
    if (already && typeof already.init === "function") {
      resolve(already);
      return;
    }
    const s = document.createElement("script");
    s.src = MAPKIT_SRC;
    s.crossOrigin = "anonymous";
    s.async = true;
    s.onload = () => {
      const kit = (window as unknown as WithMapKit).mapkit;
      if (kit) resolve(kit);
      else reject(new Error("MapKit JS loaded without its API"));
    };
    s.onerror = () => {
      s.remove();
      reject(new Error("MapKit JS could not be loaded"));
    };
    document.head.appendChild(s);
  });
}

/** The suggester for this page load, on the browser's own fetch, timers and script loader. */
export function createBrowserSuggester(): AddressSuggester | null {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return null;
  return createSuggester({
    fetch: (url, init) => window.fetch(url, init),
    loadMapKit: loadMapKitScript,
    now: () => Date.now(),
    setTimeout: (f, ms) => window.setTimeout(f, ms),
    clearTimeout: (t) => window.clearTimeout(t as number),
    AbortController: window.AbortController,
  });
}
